import { Head, usePage, router, Link } from '@inertiajs/react';
import { ThemeProvider } from '@/components/theme-provider';
import { Topbar } from '@/components/nav/Topbar';
import FlashBanner from '@/components/ui/flash-banner';
import SubscriptionBanner, {
  type SubscriptionBannerPayload,
} from '@/components/subscription/SubscriptionBanner';
import { useNavItems } from '@/components/nav/useNavItems';
import { useCallback, useEffect, useState } from 'react';
import type { ReactNodeCompat } from '@/types/react-compat';
import NewPatientModal from '@/components/patient/NewPatientModal';
import Logo from '@/components/logo';
import {
  PATIENT_CREATE_EVENT,
  PATIENT_CREATE_SESSION_KEY,
  PATIENT_CREATED_EVENT,
  type PatientCreatedEventDetail,
} from '@/lib/patientEvents';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
} from '@/components/ui/sidebar';
import TherapistSupportMenuItem from '@/components/support/TherapistSupportMenuItem';
import posthog from 'posthog-js';

export interface LayoutProps {
  children?: ReactNodeCompat;
  mainHeader?: ReactNodeCompat;
  isAdmin?: boolean;
  user?: { name: string; avatarUrl?: string };
  title?: string;
  scrollMode?: 'page' | 'content';
  hideSubscriptionBanner?: boolean;
}

type FlashMessages = Partial<Record<'notice' | 'success' | 'alert' | 'error', string>>;
const CLINICAL_NOTE_DRAFT_STORAGE_PREFIX = 'emovia:clinical-notes:drafts:';

function clearClinicalNoteDraftsFromStorage() {
  if (typeof window === 'undefined') return;

  Object.keys(window.localStorage)
    .filter((key) => key.startsWith(CLINICAL_NOTE_DRAFT_STORAGE_PREFIX))
    .forEach((key) => {
      window.localStorage.removeItem(key);
    });
}

export default function Layout({
  children,
  isAdmin,
  mainHeader,
  scrollMode = 'page',
  hideSubscriptionBanner = false,
}: LayoutProps) {
  const role = isAdmin ? 'therapist' : 'patient';
  const { menuSections } = useNavItems(role);
  const page = usePage<{
    flash?: FlashMessages;
    subscriptionSummary?: {
      banner?: SubscriptionBannerPayload | null;
      clientCount?: number;
      clientLimit?: number | null;
    };
    patientAccessBlocked?: boolean;
    patientAccessBlockedMessage?: string | null;
  }>();
  const flash = page.props.flash ?? {};
  const subscriptionSummary = page.props.subscriptionSummary;
  const patientAccessBlocked = page.props.patientAccessBlocked;
  const patientAccessBlockedMessage = page.props.patientAccessBlockedMessage;
  const path = (page?.url as string) || '';
  const [isNewPatientModalOpen, setIsNewPatientModalOpen] = useState(false);

  useEffect(() => {
    const handler = () => setIsNewPatientModalOpen(true);
    window.addEventListener(PATIENT_CREATE_EVENT, handler);
    return () => window.removeEventListener(PATIENT_CREATE_EVENT, handler);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (
      window.sessionStorage.getItem(PATIENT_CREATE_SESSION_KEY) === '1' &&
      path.startsWith('/therapists/patients')
    ) {
      window.sessionStorage.removeItem(PATIENT_CREATE_SESSION_KEY);
      setIsNewPatientModalOpen(true);
    }
  }, [path]);

  const handlePatientCreated = useCallback(
    (patient: PatientCreatedEventDetail['patient'], temporaryPassword: string) => {
      const detail: PatientCreatedEventDetail = { patient, temporaryPassword };
      window.dispatchEvent(new CustomEvent(PATIENT_CREATED_EVENT, { detail }));
    },
    [],
  );

  const onAction = (key: string) => {
    if (key === 'logout') {
      const posthogLoaded = (posthog as unknown as { __loaded?: boolean }).__loaded;
      if (posthogLoaded) {
        posthog.reset();
      }
      clearClinicalNoteDraftsFromStorage();
      // Esegui logout con form POST + _method=delete per forzare navigazione full-page
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = role === 'patient' ? '/patients/sign_out' : '/therapists/sign_out';
      const method = document.createElement('input');
      method.type = 'hidden';
      method.name = '_method';
      method.value = 'delete';
      const token = document.createElement('input');
      token.type = 'hidden';
      token.name = 'authenticity_token';
      token.value =
        (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content || '';
      form.appendChild(method);
      form.appendChild(token);
      document.body.appendChild(form);
      form.submit();
      return true;
    }
    if (key === 'new_patient') {
      if (path.startsWith('/therapists/patients')) {
        window.dispatchEvent(new CustomEvent(PATIENT_CREATE_EVENT));
      } else {
        if (typeof window !== 'undefined') {
          window.sessionStorage.setItem(PATIENT_CREATE_SESSION_KEY, '1');
        }
        router.visit('/therapists/patients');
      }
      return true;
    }
    return false;
  };
  return (
    <ThemeProvider defaultTheme="light" storageKey="vite-ui-theme">
      <SidebarProvider defaultOpen>
        <Head title={`Emovia${isAdmin ? ' · Psicologo' : ''}`} />
        <div className="h-screen overflow-hidden w-full">
          {/* Mobile topbar */}
          <div className="lg:hidden">
            <Topbar
              items={menuSections}
              title={isAdmin ? 'Emovia · Psicologo' : 'Emovia'}
              onAction={onAction}
              currentPath={path}
            />
          </div>
          {/* Sidebar desktop */}
          <div className="hidden lg:block">
            <Sidebar collapsible="icon">
              <SidebarHeader className="pb-6 border-b border-border">
                <div className="flex items-center gap-3 px-2 pt-2">
                  <span className="sr-only">Emovia</span>
                  <Logo className="h-6 w-32 shrink-0" />
                </div>
              </SidebarHeader>

              <SidebarContent>
                {menuSections
                  .filter((section) => !section.pullDown)
                  .map((section) => (
                    <SidebarGroup key={section.name}>
                      <SidebarGroupLabel className="uppercase">{section.name}</SidebarGroupLabel>
                      <SidebarGroupContent>
                        <SidebarMenu>
                          {section.items.map((item) => {
                            const method = item.method ?? 'get';
                            const isInteractiveAction = item.key === 'new_patient';
                            const isActive =
                              !isInteractiveAction &&
                              method === 'get' &&
                              (path === item.href ||
                                (item.key === 'new_chat' &&
                                  path.startsWith('/patients/chats/new')) ||
                                (item.key === 'patients' &&
                                  path.startsWith('/therapists/patients')));

                            return (
                              <SidebarMenuItem key={`${item.label}-${item.href}`}>
                                <SidebarMenuButton
                                  asChild
                                  isActive={isActive}
                                  onClick={(e) => {
                                    if (onAction && onAction(item.key)) e.preventDefault();
                                  }}
                                  tooltip={item.label}
                                >
                                  <Link
                                    href={item.href}
                                    method={method}
                                    aria-current={isActive ? 'page' : undefined}
                                  >
                                    {item.icon && <item.icon className="size-4" />}
                                    <span>{item.label}</span>
                                  </Link>
                                </SidebarMenuButton>
                              </SidebarMenuItem>
                            );
                          })}
                        </SidebarMenu>
                      </SidebarGroupContent>
                    </SidebarGroup>
                  ))}
              </SidebarContent>

              <SidebarFooter>
                {isAdmin && (
                  <SidebarGroup>
                    <SidebarGroupLabel className="uppercase">Aiuto</SidebarGroupLabel>
                    <SidebarGroupContent>
                      <SidebarMenu>
                        <TherapistSupportMenuItem />
                      </SidebarMenu>
                    </SidebarGroupContent>
                  </SidebarGroup>
                )}
                {menuSections
                  .filter((section) => section.pullDown)
                  .map((section) => (
                    <SidebarGroup key={section.name}>
                      <SidebarGroupLabel className="uppercase">{section.name}</SidebarGroupLabel>
                      <SidebarGroupContent>
                        <SidebarMenu>
                          {section.items.map((item) => {
                            const method = item.method ?? 'get';
                            const isActive = method === 'get' && path === item.href;
                            return (
                              <SidebarMenuItem key={`${item.label}-${item.href}`}>
                                <SidebarMenuButton
                                  asChild
                                  isActive={isActive}
                                  onClick={(e) => {
                                    if (onAction && onAction(item.key)) e.preventDefault();
                                  }}
                                  tooltip={item.label}
                                >
                                  <Link
                                    href={item.href}
                                    method={method}
                                    aria-current={isActive ? 'page' : undefined}
                                  >
                                    {item.icon && <item.icon className="size-4" />}
                                    <span>{item.label}</span>
                                  </Link>
                                </SidebarMenuButton>
                              </SidebarMenuItem>
                            );
                          })}
                        </SidebarMenu>
                      </SidebarGroupContent>
                    </SidebarGroup>
                  ))}
              </SidebarFooter>
            </Sidebar>
          </div>
          {!isAdmin && patientAccessBlocked ? (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm">
              <div className="w-[92%] max-w-md rounded-xl border border-border bg-card p-6 text-center shadow-xl">
                <p className="text-base font-semibold text-foreground">Accesso sospeso</p>
                <p className="mt-2 text-sm text-muted-foreground">
                  {patientAccessBlockedMessage || 'Accesso sospeso.'}
                </p>
              </div>
            </div>
          ) : null}
          {/* Main */}
          <main
            className={`flex h-screen flex-col items-stretch lg:pl-(--sidebar-width) bg-muted/30 ${
              scrollMode === 'page' ? 'overflow-y-auto' : 'overflow-hidden'
            }`}
          >
            <div
              className={`w-full max-w-6xl mx-auto px-4 py-4 sm:px-6 lg:px-8 flex flex-col ${
                scrollMode === 'page' ? '' : 'h-full min-h-0'
              }`}
            >
              {mainHeader}
              {isAdmin && subscriptionSummary?.banner && !hideSubscriptionBanner ? (
                <SubscriptionBanner
                  className="mt-2 mb-6"
                  banner={subscriptionSummary.banner}
                  clientCount={subscriptionSummary.clientCount ?? 0}
                  clientLimit={subscriptionSummary.clientLimit ?? null}
                />
              ) : null}
              {Boolean(flash.notice || flash.success || flash.alert || flash.error) && (
                <FlashBanner
                  message={flash.notice ?? flash.success ?? flash.alert ?? flash.error ?? ''}
                  tone={flash.alert || flash.error ? 'error' : flash.success ? 'success' : 'info'}
                  className="mt-4 w-full"
                />
              )}
              <div
                className={scrollMode === 'page' ? 'w-full' : 'w-full flex-1 min-h-0 flex flex-col'}
              >
                {children}
              </div>
            </div>
          </main>
        </div>
        <NewPatientModal
          open={isNewPatientModalOpen}
          onOpenChange={(open) => setIsNewPatientModalOpen(open)}
          onCreated={handlePatientCreated}
        />
      </SidebarProvider>
    </ThemeProvider>
  );
}
